import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Package, 
  Search, 
  Plus, 
  Edit, 
  BarChart3,
  Camera,
  QrCode,
  Filter,
  SortAsc,
  Grid3X3,
  List
} from 'lucide-react';
import { SwipeGestures } from './SwipeGestures';

interface Product {
  id: string;
  name: string;
  price: number;
  stock: number;
  category: string;
  sku: string;
  status: 'active' | 'inactive' | 'low_stock';
  image?: string;
}

const SAMPLE_PRODUCTS: Product[] = [
  { id: '1', name: 'MacBook Pro 16"', price: 2499.99, stock: 5, category: 'Laptops', sku: 'MBP16-001', status: 'active' },
  { id: '2', name: 'iPhone 15 Pro', price: 999.99, stock: 2, category: 'Phones', sku: 'IP15P-001', status: 'low_stock' },
  { id: '3', name: 'AirPods Pro', price: 249.99, stock: 15, category: 'Audio', sku: 'APP-001', status: 'active' },
  { id: '4', name: 'iPad Air', price: 599.99, stock: 0, category: 'Tablets', sku: 'IPA-001', status: 'inactive' },
  { id: '5', name: 'Apple Watch', price: 399.99, stock: 8, category: 'Wearables', sku: 'AW-001', status: 'active' },
  { id: '6', name: 'Magic Keyboard', price: 329.99, stock: 12, category: 'Accessories', sku: 'MK-001', status: 'active' }
];

const CATEGORIES = ['Todos', 'Laptops', 'Phones', 'Audio', 'Tablets', 'Wearables', 'Accessories'];

export function MobileProductsView() {
  const [products] = useState<Product[]>(SAMPLE_PRODUCTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'Todos' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getStatusColor = (status: Product['status']) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'low_stock': return 'text-warning';
      case 'inactive': return 'text-muted-foreground';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusText = (status: Product['status']) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'low_stock': return 'Stock Bajo';
      case 'inactive': return 'Inactivo';
      default: return 'Desconocido';
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold">Productos</h1>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}>
              {viewMode === 'grid' ? <List className="h-4 w-4" /> : <Grid3X3 className="h-4 w-4" />}
            </Button>
            <Button size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Búsqueda */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre o SKU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
          />
          <Button variant="ghost" size="sm" className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0">
            <QrCode className="h-4 w-4" />
          </Button>
        </div>

        {/* Filtros */}
        {showFilters && (
          <div className="space-y-3 mb-4">
            <div className="flex space-x-2 overflow-x-auto pb-2">
              {CATEGORIES.map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className="whitespace-nowrap flex-shrink-0"
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex space-x-4 text-sm text-muted-foreground">
          <span>{filteredProducts.length} productos</span>
          <span>•</span>
          <span>{filteredProducts.filter(p => p.status === 'active').length} activos</span>
          <span>•</span>
          <span>{filteredProducts.filter(p => p.status === 'low_stock').length} stock bajo</span>
        </div>
      </div>

      {/* Contenido */}
      <div className="flex-1 overflow-auto p-4">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-2 gap-3">
            {filteredProducts.map(product => (
              <SwipeGestures
                key={product.id}
                onSwipeLeft={() => console.log('Edit', product.id)}
                onSwipeRight={() => console.log('View stats', product.id)}
                className="h-full"
              >
                <Card className="apple-card h-full cursor-pointer active:scale-95 transition-transform">
                  <CardContent className="p-3">
                    <div className="aspect-square bg-muted rounded-lg mb-3 flex items-center justify-center relative">
                      <Package className="h-8 w-8 text-muted-foreground" />
                      <Badge 
                        className={`absolute top-1 right-1 text-xs ${getStatusColor(product.status)}`}
                        variant={product.status === 'active' ? 'default' : 'secondary'}
                      >
                        {product.stock}
                      </Badge>
                    </div>
                    
                    <div>
                      <h3 className="font-medium text-sm leading-tight mb-1">{product.name}</h3>
                      <p className="text-xs text-muted-foreground mb-1">{product.sku}</p>
                      <p className="text-xs text-muted-foreground mb-2">{product.category}</p>
                      
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-sm">${product.price}</span>
                        <Badge 
                          variant={product.status === 'active' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {getStatusText(product.status)}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </SwipeGestures>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredProducts.map(product => (
              <SwipeGestures
                key={product.id}
                onSwipeLeft={() => console.log('Edit', product.id)}
                onSwipeRight={() => console.log('View stats', product.id)}
              >
                <Card className="apple-card cursor-pointer active:scale-[0.98] transition-transform">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                        <Package className="h-6 w-6 text-muted-foreground" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm truncate">{product.name}</h3>
                            <p className="text-xs text-muted-foreground">{product.sku} • {product.category}</p>
                          </div>
                          <div className="text-right ml-2">
                            <p className="font-bold text-sm">${product.price}</p>
                            <Badge 
                              variant={product.status === 'active' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {product.stock} unidades
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mt-2">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${getStatusColor(product.status)}`}
                          >
                            {getStatusText(product.status)}
                          </Badge>
                          
                          <div className="flex space-x-1">
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <BarChart3 className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <Camera className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </SwipeGestures>
            ))}
          </div>
        )}
      </div>

      {/* FAB para acciones rápidas */}
      <div className="fixed bottom-20 right-4 md:bottom-4">
        <Button size="lg" className="h-14 w-14 rounded-full shadow-lg">
          <QrCode className="h-6 w-6" />
        </Button>
      </div>
    </div>
  );
}